import type { ItemList, Offer, Product, WithContext } from 'schema-dts';

import { appConfig } from '../../../../appConfig';
import type { ApiProduct } from '../../../../models/ecommerce/product/ApiProduct';
import { Helpers } from '../../../../utility/helpers';
import type { PriceType } from '../../../common/models/priceData/priceData';
import { getCloudinaryUrl } from '../../../common/utils/cloudinaryImageUtils';
import { buildAbsoluteUrl } from '../../utils/urlEcommerceUtils';

/**
 *
 * @param product the product to be used for the offer.
 * @param priceType price type to be used for the offer.
 * @param area Specifices if the product is being used in a list or product detail page.
 * @param hostname the hostname for the site
 * @returns json+ld object for the offer.
 */
export const generateOfferJsonLd = (
    product: ApiProduct,
    priceType: PriceType,
    hostname: string,
) => {
    const offer: Offer = {
        '@type': 'Offer',
        price: `${product.priceData && Helpers.getPrice(product.priceData, priceType)}`,
        priceCurrency: 'SEK',
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        url: buildAbsoluteUrl(product.url),
    };

    offer.deliveryLeadTime = {
        '@type': 'QuantitativeValue',
        value: 0,
        unitCode: 'DAY',
        description: 'Vissa butiker erbjuder expressleveranser',
    };
    offer.shippingDetails = {
        '@type': 'OfferShippingDetails',
        shippingRate: {
            '@type': 'MonetaryAmount',
            maxValue: 79,
            minValue: 0,
            currency: 'SEK',
        },
        shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'SE',
        },
        deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 1,
                unitCode: 'DAY',
            },
            transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 1,
                unitCode: 'DAY',
            },
        },
    };

    offer.hasMerchantReturnPolicy = {
        '@type': 'MerchantReturnPolicy',
        returnFees: 'https://schema.org/FreeReturn',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        applicableCountry: 'SE',
        merchantReturnLink: `https://${hostname}${appConfig.coopSettings.myCoop.lastOrdersUrl}`,
        itemCondition: 'NewCondition',
        merchantReturnDays: 30,
        customerRemorseReturnShippingFeesAmount: {
            '@type': 'MonetaryAmount',
            name: ' 14 dagar från leverans ångra köp av varor.',
            value: '0',
            currency: 'SEK',
        },
        refundType: 'https://schema.org/FullRefund',
        itemDefectReturnFees: 'https://schema.org/FreeReturn',
        customerRemorseReturnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
    };

    return offer;
};

/**
 *
 * @param product the product to be used for the product.
 * @param priceType price type to be used for the product.
 * @param area Specifices if the product is being used in a list or product detail page.
 * @param hostname the hostname for the site
 * @returns json+ld object for the product.
 */
export const generateProductJsonLd = (
    product: ApiProduct,
    priceType: PriceType,
    hostname: string,
) => {
    const jsonLd: WithContext<Product> = {
        '@type': 'Product',
        name: product.name,
        brand: product.manufacturer,
        countryOfOrigin: product.details?.countryOfOriginCodes?.join(','),
        image: product.image?.url
            ? [
                  getCloudinaryUrl(
                      product.image.url,
                      'e_sharpen,f_auto,fl_clip,fl_progressive,q_90,c_lpad,g_center,h_660,w_660',
                  ),
                  getCloudinaryUrl(
                      product.image.url,
                      'e_sharpen,f_auto,fl_clip,fl_progressive,q_90,c_lpad,g_center,h_371,w_660',
                  ),
                  getCloudinaryUrl(
                      product.image.url,
                      'e_sharpen,f_auto,fl_clip,fl_progressive,q_90,c_lpad,g_center,h_440,w_660',
                  ),
              ].filter(Boolean)
            : [],
        offers: generateOfferJsonLd(product, priceType, hostname),
        sku: product.identifier,
        '@context': 'https://schema.org',
    };
    const isNicotineProduct = product.details.isAgeLimited;
    const isPharmaceutical = product.details?.pharmaceuticalData?.isPharmaceutical;
    jsonLd.description = product.details?.description;
    // If i understand GTIN correctly, 2 series is "Used to issue Restricted Circulation Numbers within a geographic region." and 5 series "is reserved for future use".
    if (product.ean.length === 13 && !product.ean.startsWith('2') && !product.ean.startsWith('5')) {
        jsonLd.gtin13 = product.ean;
    }

    if (isNicotineProduct || isPharmaceutical) {
        jsonLd.audience = {
            '@type': 'PeopleAudience',
            requiredMinAge: 18,
        };
    }

    return jsonLd;
};

export const generateItemListJsonLd = (
    urls: string[],
    optional?: {
        numberOfItems?: number;
        name?: string;
    },
): WithContext<ItemList> => {
    const list: WithContext<ItemList> = {
        '@type': 'ItemList',
        '@context': 'https://schema.org',
        itemListElement: urls.map((url, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url,
        })),
    };
    if (optional) {
        if (optional.numberOfItems) {
            list.numberOfItems = optional.numberOfItems;
        }
        if (optional.name) {
            list.name = optional.name;
        }
    }
    return list;
};
